import { useCallback, useEffect, useRef, useState } from 'react';
import { detectLabelZones, type Region, type ZoneProposal } from '../lib/detectLabelZones';
import type { Product } from '@visionqc/shared';
import { api } from '../api/client';
import { useIsAdmin } from '../api/auth';
import { useImageUrl } from '../api/useImageUrl';
import { useProducts } from '../api/useProducts';
import { fmtTime } from '../lib/format';

/**
 * Label inspection.
 *
 * A different kind of check from the anomaly model, and worth saying so on the
 * page: that one learns what normal looks like from hundreds of images, which
 * cannot work for print — a wrong lot code is not anomalous, it is the wrong
 * text. This compares against one approved sample, so it works the same
 * afternoon a customer sends a photo.
 *
 * Three steps, in the order somebody does them: upload the good label, mark
 * what matters on it, then test a pack against it.
 */

export interface LabelZone {
  id: string;
  check: 'match' | 'present' | 'text' | 'code';
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
  pattern?: string | null;
  expected?: string | null;
  threshold?: number | null;
}

export interface LabelReference {
  id: number;
  productId: number;
  name: string;
  imageKey: string;
  zones: LabelZone[];
  minAlign: number;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
}

export interface LabelResult {
  id: string;
  verdict: string;
  aligned: boolean;
  align_score: number;
  reason: string;
  overlay_key: string | null;
  latency_ms: number;
  zones: Array<{
    id: string;
    label: string;
    check: string;
    ok: boolean;
    score: number;
    detail: string;
    found: string | null;
  }>;
}

const CHECKS: Array<{ id: LabelZone['check']; name: string; hint: string }> = [
  { id: 'match', name: 'Must match', hint: 'Same print as the reference — catches missing or smudged printing.' },
  { id: 'present', name: 'Must be printed', hint: 'Ink is there, but the content may differ — for a batch code that changes.' },
  { id: 'code', name: 'Barcode / QR', hint: 'Must decode. Optionally to an exact value.' },
  { id: 'text', name: 'Readable text', hint: 'Read it and check it against a pattern or an exact value.' },
];

const CHECK_COLOUR: Record<string, string> = {
  match: '#3b82f6',
  present: '#14b8a6',
  code: '#a855f7',
  text: '#f59e0b',
};

export function LabelsView() {
  const isAdmin = useIsAdmin();
  const { products } = useProducts();
  const [productId, setProductId] = useState<number | null>(null);
  const [references, setReferences] = useState<LabelReference[]>([]);
  const [history, setHistory] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (productId === null && products.length > 0) setProductId(products[0]!.id);
  }, [products, productId]);

  const load = useCallback(async () => {
    if (!productId) return;
    const [refs, hist] = await Promise.all([
      api.listLabelReferences(productId).catch(() => []),
      api.listLabelInspections(productId, 20).catch(() => []),
    ]);
    setReferences(refs);
    setHistory(hist);
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = references.find((r) => r.active) ?? null;

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Etiket AI</h1>
        <select
          value={productId ?? ''}
          onChange={(e) => setProductId(e.target.value ? Number(e.target.value) : null)}
        >
          {products.map((p: Product) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          {/* Said here because picking the wrong tool for the job is the most
              expensive mistake available on this page. */}
          Compares a pack against <strong>one approved label</strong>. Use this for print — a wrong
          lot code is not a visual anomaly, so the trained model cannot catch it. Use{' '}
          <strong>Training</strong> instead for surface defects like scratches and dents.
        </p>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>}

      {!productId ? (
        <div className="panel muted">Pick a product first.</div>
      ) : !active ? (
        <UploadReference
          productId={productId}
          isAdmin={isAdmin}
          onUploaded={async () => {
            setNotice('Reference uploaded. Now mark what should be checked on it.');
            await load();
          }}
          onError={setError}
        />
      ) : (
        <>
          <UploadReference
            productId={productId}
            isAdmin={isAdmin}
            replace
            onUploaded={async () => {
              setNotice('New reference uploaded. Mark what should be checked on it.');
              await load();
            }}
            onError={setError}
          />
          <ZoneEditor
            reference={active}
            isAdmin={isAdmin}
            onSaved={async (n) => {
              setNotice(`${n} zone${n === 1 ? '' : 's'} saved.`);
              await load();
            }}
            onError={setError}
          />
          <TestPanel productId={productId} reference={active} onTested={load} onError={setError} />
        </>
      )}

      {references.length > 1 && (
        <div className="panel" style={{ marginTop: 16 }}>
          <strong>Earlier references</strong>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Kept when the packaging changes — the old one is what explains why last month's
            rejects looked the way they did.
          </p>
          <table style={{ marginTop: 8 }}>
            <tbody>
              {references
                .filter((r) => !r.active)
                .map((r) => (
                  <tr key={r.id}>
                    <td>{r.name}</td>
                    <td className="muted">{r.zones.length} zones</td>
                    <td className="muted">{fmtTime(r.createdAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn sm"
                        disabled={!isAdmin}
                        onClick={async () => {
                          await api.activateLabelReference(r.id);
                          await load();
                        }}
                      >
                        Make active
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {history.length > 0 && <History rows={history} />}
    </div>
  );
}

function UploadReference({
  productId,
  isAdmin,
  onUploaded,
  onError,
  replace = false,
}: {
  productId: number;
  isAdmin: boolean;
  onUploaded: () => void | Promise<void>;
  onError: (m: string) => void;
  /**
   * Compact mode, shown when a reference already exists — a way to scan a fresh
   * label and make it the active one. Without it there was no route in the UI to
   * replace a reference once set, only to switch between ones already uploaded.
   */
  replace?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const picker = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      hidden
      onChange={async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setBusy(true);
        try {
          await api.uploadLabelReference(productId, file);
          await onUploaded();
        } catch (err) {
          onError((err as Error).message);
        } finally {
          setBusy(false);
        }
      }}
    />
  );

  // Replace mode: a slim bar above the editor. The upload makes the new label the
  // active reference and keeps the current one under "Earlier references" (the
  // backend deactivates rather than deletes), so nothing is lost by trying one.
  if (replace) {
    return (
      <div
        className="panel"
        style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <span className="muted" style={{ fontSize: 13 }}>
          Scanned a better photo? Upload it as the new reference — the current one is kept under
          “Earlier references”.
        </span>
        {picker}
        <button
          className="btn sm"
          disabled={!isAdmin || busy}
          onClick={() => inputRef.current?.click()}
          title={!isAdmin ? 'Requires the ADMIN role' : undefined}
        >
          {busy ? 'Uploading…' : 'Upload a new reference'}
        </button>
      </div>
    );
  }

  return (
    <div className="panel">
      <strong>1 · The approved label</strong>
      <p className="muted" style={{ fontSize: 13, marginTop: 4 }}>
        One photo of a pack that is definitely correct. Everything else is compared against this,
        so shoot it the way the camera on the line will see it — same distance, same lighting.
      </p>
      {picker}
      <button
        className="btn primary"
        style={{ marginTop: 12 }}
        disabled={!isAdmin || busy}
        onClick={() => inputRef.current?.click()}
      >
        {busy ? 'Uploading…' : 'Upload reference label'}
      </button>
      {!isAdmin && (
        <span className="muted" style={{ fontSize: 12, marginLeft: 10 }}>
          Requires the ADMIN role.
        </span>
      )}
    </div>
  );
}

/**
 * Draw the zones on the reference.
 *
 * Same drag-to-mark interaction as the annotator, because it is the same
 * gesture doing the same thing and an operator should not have to learn it
 * twice.
 */
function ZoneEditor({
  reference,
  isAdmin,
  onSaved,
  onError,
}: {
  reference: LabelReference;
  isAdmin: boolean;
  onSaved: (n: number) => void | Promise<void>;
  onError: (m: string) => void;
}) {
  const url = useImageUrl(reference.imageKey);
  const imgRef = useRef<HTMLImageElement>(null);
  const [zones, setZones] = useState<LabelZone[]>(reference.zones ?? []);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<LabelZone | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // Proposals live apart from zones on purpose — see detectLabelZones.ts. They
  // are drawn, they are never saved, and they become zones only by a click.
  const [proposals, setProposals] = useState<ZoneProposal[]>([]);
  // Which proposal the pointer is over in the list, so its box on the label
  // lights up — the link between the suggestion and the place it points to.
  const [highlight, setHighlight] = useState<string | null>(null);
  const [scanning, setScanning] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);

  /**
   * Crop mode: the same drag gesture, but around the label instead of one
   * field, and it feeds the scan rather than making a zone.
   *
   * It exists because a reference is often a photo of a pack on a bench, and
   * OCR run over the whole frame reads the desk as confidently as the print.
   * The crop is remembered, so "Scan again" re-uses it — a person tunes the box
   * once, not once per attempt.
   */
  const [cropMode, setCropMode] = useState(false);
  const [cropDraft, setCropDraft] = useState<LabelZone | null>(null);
  const [region, setRegion] = useState<Region | null>(null);
  const cropStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setZones(reference.zones ?? []);
    setSelected(null);
    setProposals([]);
    setScanned(false);
    setCropMode(false);
    setCropDraft(null);
    setRegion(null);
  }, [reference.id, reference.zones]);

  const scan = useCallback(
    async (withRegion?: Region | null) => {
      if (!url) return;
      const use = withRegion !== undefined ? withRegion : region;
      setScanning('starting');
      setProposals([]);
      try {
        const found = await detectLabelZones(url, (m) => setScanning(m), use);
        /*
          Every proposal is shown, even where one overlaps a zone already saved.

          The earlier version hid any proposal that overlapped an existing zone,
          to avoid offering a field twice. It had a failure that looked exactly
          like a broken scan: one big saved box — someone's "whole label" test
          zone — overlaps everything, so every proposal was suppressed and the
          panel said "found nothing" while the codes sat there plainly decoded.
          Proposals are drawn dashed and distinct from saved zones, so a
          genuine duplicate is a click to discard, not a reason to hide the lot.
        */
        /*
          Drop a proposal that is already a saved zone, so a re-scan offers only
          what is missing rather than a second copy of everything.

          Measured by IoU (overlap ÷ union), not bare overlap. Bare overlap was
          removed once because one big "whole label" zone overlapped every
          proposal and suppressed the lot; IoU does not have that failure — a
          small proposal inside a huge zone has a tiny union-relative overlap and
          survives, while a genuine duplicate (a box the same size and place)
          scores high and is dropped.
        */
        const iou = (a: ZoneProposal | LabelZone, b: LabelZone) => {
          const ix = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
          const iy = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
          const inter = ix * iy;
          const union = a.w * a.h + b.w * b.h - inter;
          return union > 0 ? inter / union : 0;
        };
        setProposals(found.zones.filter((p) => !zones.some((z) => iou(p, z) > 0.45)));
        /*
          Only surface an auto-crop that EARNED it — one that turned up a field
          the whole-frame scan could not, not merely one that ran.

          Judged by result, not intent. On an opaque label on a bench the
          auto-crop isolates the label and finds its text; on a transparent
          bottle, print shows through from every side and the auto-crop is a
          loose box that still yields only the two codes. Presenting that box as
          the answer would hide the "draw one yourself" hint behind a crop that
          is visibly wrong — worse than showing nothing. So the box is kept only
          when it paid for itself, and the bottle falls through to the hint.
        */
        const gained = found.zones.some((z) => z.check !== 'code');
        if (found.autoCropped && found.usedRegion && gained) setRegion(found.usedRegion);
      } catch (e) {
        onError((e as Error).message);
      } finally {
        setScanning(null);
        setScanned(true);
      }
    },
    [url, zones, region, onError],
  );

  /**
   * Scan as soon as the reference appears, without being asked.
   *
   * Only when nothing is marked yet: re-running it over a finished set would
   * churn through the OCR for no reason and put proposals next to zones a
   * person has already thought about.
   */
  useEffect(() => {
    if (url && !scanned && (reference.zones?.length ?? 0) === 0) void scan(null);
    // scan is intentionally omitted: it changes identity as `zones` grows, and
    // depending on it would re-fire the auto-scan every time a zone is added.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, scanned, reference.zones]);

  const toNorm = (cx: number, cy: number) => {
    const r = imgRef.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (cx - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (cy - r.top) / r.height)),
    };
  };

  const all = draft ? [...zones, draft] : zones;

  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>2 · What should be checked</strong>
        <button
          className="btn primary sm"
          disabled={!isAdmin || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.setLabelZones(reference.id, zones);
              await onSaved(zones.length);
            } catch (e) {
              onError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Saving…' : 'Save zones'}
        </button>
      </div>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          {cropMode
            ? 'Drag a box around the label itself. Only that area is read — the bench and the background are left out.'
            : 'Drag a box over each thing that matters — the logo, the lot code, the barcode. Then say what kind of check it is.'}
        </p>
        <button
          className="btn sm"
          disabled={!isAdmin || !!scanning}
          onClick={() => {
            if (cropMode) {
              setCropMode(false);
              setCropDraft(null);
              return;
            }
            setCropMode(true);
            setCropDraft(null);
          }}
          style={{ whiteSpace: 'nowrap' }}
          title="Read only the part of the photo that is the label, when the reference is a photo with background around it"
        >
          {cropMode ? 'Cancel' : region ? 'Redo region' : 'Scan a region'}
        </button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 16, marginTop: 12 }}>
        <div
          style={{
            position: 'relative',
            // Hug the image's height rather than stretching to the grid row. The
            // overlay boxes are positioned in % of THIS container, so if it
            // stretched taller than the image (when the zone list on the right
            // grows), every box would slide down off the label. alignSelf keeps
            // the container the height of the image, so the boxes stay put.
            alignSelf: 'start',
            background: '#000',
            borderRadius: 8,
            cursor: 'crosshair',
            userSelect: 'none',
            // A ring while cropping, so it is obvious the next drag means
            // something different from marking a field.
            outline: cropMode ? '2px solid var(--accent)' : 'none',
            outlineOffset: 2,
          }}
          onDragStart={(e) => e.preventDefault()}
          onMouseDown={(e) => {
            if (!isAdmin) return;
            e.preventDefault();
            const p = toNorm(e.clientX, e.clientY);
            if (cropMode) {
              cropStart.current = p;
              setCropDraft({ id: 'crop', check: 'match', x: p.x, y: p.y, w: 0, h: 0, label: '' });
              return;
            }
            dragStart.current = p;
            setDraft({ id: `z${Date.now()}`, check: 'match', x: p.x, y: p.y, w: 0, h: 0, label: '' });
          }}
          onMouseMove={(e) => {
            if (cropMode && cropStart.current) {
              const p = toNorm(e.clientX, e.clientY);
              const s = cropStart.current;
              setCropDraft((d) =>
                d
                  ? { ...d, x: Math.min(s.x, p.x), y: Math.min(s.y, p.y), w: Math.abs(p.x - s.x), h: Math.abs(p.y - s.y) }
                  : d,
              );
              return;
            }
            if (!dragStart.current) return;
            const p = toNorm(e.clientX, e.clientY);
            const s = dragStart.current;
            setDraft((d) =>
              d
                ? {
                    ...d,
                    x: Math.min(s.x, p.x),
                    y: Math.min(s.y, p.y),
                    w: Math.abs(p.x - s.x),
                    h: Math.abs(p.y - s.y),
                  }
                : d,
            );
          }}
          onMouseUp={() => {
            if (cropMode) {
              cropStart.current = null;
              if (cropDraft && cropDraft.w > 0.02 && cropDraft.h > 0.02) {
                const r: Region = { x: cropDraft.x, y: cropDraft.y, w: cropDraft.w, h: cropDraft.h };
                setRegion(r);
                setCropMode(false);
                void scan(r);
              } else {
                setCropDraft(null);
              }
              return;
            }
            if (draft && draft.w > 0.01 && draft.h > 0.01) {
              setZones((z) => [...z, { ...draft, label: `Zone ${z.length + 1}` }]);
              setSelected(zones.length);
            }
            setDraft(null);
            dragStart.current = null;
          }}
          onMouseLeave={() => {
            setDraft(null);
            dragStart.current = null;
            cropStart.current = null;
          }}
        >
          {url ? (
            <img
              ref={imgRef}
              src={url}
              alt="reference label"
              style={{ display: 'block', width: '100%', borderRadius: 8 }}
              draggable={false}
            />
          ) : (
            <div style={{ height: 360 }} />
          )}

          {all.map((z, i) => {
            const colour = CHECK_COLOUR[z.check] ?? '#3b82f6';
            const isSel = i === selected;
            return (
              <div
                key={z.id}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setSelected(i === selected ? null : i);
                }}
                style={{
                  position: 'absolute',
                  left: `${z.x * 100}%`,
                  top: `${z.y * 100}%`,
                  width: `${z.w * 100}%`,
                  height: `${z.h * 100}%`,
                  // Dark halo under the colour, so an edge stays legible on a
                  // white label and a black one alike.
                  boxShadow: '0 0 0 3px rgba(0,0,0,0.75)',
                  border: `2px ${isSel ? 'dashed' : 'solid'} ${colour}`,
                  background: `color-mix(in srgb, ${colour} 16%, transparent)`,
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: -20,
                    left: 0,
                    background: colour,
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 4,
                    whiteSpace: 'nowrap',
                    textShadow: '0 1px 2px rgba(0,0,0,0.9)',
                  }}
                >
                  {z.label || 'unnamed'}
                </span>
              </div>
            );
          })}

          {/*
            Proposals, drawn under their own rules: dashed, unfilled, and
            never selectable as a zone. A person must be able to tell at a
            glance which boxes the machine guessed and which ones they own.
          */}
          {proposals.map((p) => {
            const lit = p.id === highlight;
            const colour = CHECK_COLOUR[p.check] ?? '#3b82f6';
            return (
              <div
                key={p.id}
                style={{
                  position: 'absolute',
                  left: `${p.x * 100}%`,
                  top: `${p.y * 100}%`,
                  width: `${p.w * 100}%`,
                  height: `${p.h * 100}%`,
                  boxShadow: lit ? '0 0 0 3px rgba(0,0,0,0.85)' : '0 0 0 2px rgba(0,0,0,0.6)',
                  border: `${lit ? 3 : 2}px dashed ${colour}`,
                  background: lit ? `color-mix(in srgb, ${colour} 22%, transparent)` : 'transparent',
                  pointerEvents: 'none',
                  opacity: lit ? 1 : highlight ? 0.4 : 0.9,
                  transition: 'opacity 0.1s, background 0.1s',
                }}
              />
            );
          })}

          {/*
            The crop: the box being dragged, or the one the last scan used.
            Everything outside it is dimmed, so what the scan can and cannot see
            is the same thing the eye sees.
          */}
          {(cropDraft ?? region) &&
            (() => {
              const r = cropDraft ?? region!;
              return (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      // A four-sided shadow is the standard way to dim
                      // everything but a hole without four separate elements.
                      boxShadow: `inset 0 0 0 100vmax rgba(0,0,0,0.5)`,
                      clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${r.x * 100}% ${r.y * 100}%, ${r.x * 100}% ${(r.y + r.h) * 100}%, ${(r.x + r.w) * 100}% ${(r.y + r.h) * 100}%, ${(r.x + r.w) * 100}% ${r.y * 100}%, ${r.x * 100}% ${r.y * 100}%)`,
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: `${r.x * 100}%`,
                      top: `${r.y * 100}%`,
                      width: `${r.w * 100}%`,
                      height: `${r.h * 100}%`,
                      border: '2px solid var(--accent)',
                      pointerEvents: 'none',
                    }}
                  />
                </>
              );
            })()}
        </div>

        <div className="grid" style={{ gap: 8, alignContent: 'start' }}>
          <ProposalPanel
            proposals={proposals}
            scanning={scanning}
            scanned={scanned}
            canEdit={isAdmin}
            /*
              After a whole-image scan that only turned up codes, point at the
              region tool. On a photograph the printed fields are unreadable
              until the background is cropped away — and a person staring at a
              label full of print, told there are "2 suggestions", would
              reasonably conclude the thing is broken. This says what to do next.
            */
            cropHint={!region && proposals.length > 0 && proposals.every((p) => p.check === 'code')}
            onCrop={() => {
              setCropMode(true);
              setCropDraft(null);
            }}
            onRescan={() => void scan()}
            onHover={setHighlight}
            onAccept={(p) => {
              setZones((z) => [
                ...z,
                {
                  id: `z${Date.now()}-${z.length}`,
                  check: p.check,
                  x: p.x,
                  y: p.y,
                  w: p.w,
                  h: p.h,
                  label: p.label,
                  pattern: p.pattern ?? null,
                  expected: p.expected ?? null,
                  threshold: null,
                },
              ]);
              setProposals((all2) => all2.filter((x) => x.id !== p.id));
            }}
            onDismiss={(p) => setProposals((all2) => all2.filter((x) => x.id !== p.id))}
          />

          {zones.length === 0 && proposals.length === 0 && !scanning && (
            <span className="muted" style={{ fontSize: 13 }}>
              Nothing marked yet. Drag a box on the label to start.
            </span>
          )}
          {zones.map((z, i) => (
            <div
              key={z.id}
              className="panel"
              style={{
                background: i === selected ? 'var(--panel-2)' : 'transparent',
                borderLeft: `3px solid ${CHECK_COLOUR[z.check] ?? '#3b82f6'}`,
                padding: 10,
              }}
              onClick={() => setSelected(i)}
            >
              <div className="row" style={{ gap: 8 }}>
                <input
                  value={z.label}
                  placeholder="Name, e.g. Lot code"
                  onChange={(e) =>
                    setZones((all2) => all2.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))
                  }
                  style={{ flex: 1 }}
                />
                <button
                  className="btn sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setZones((all2) => all2.filter((_, k) => k !== i));
                    setSelected(null);
                  }}
                >
                  ✕
                </button>
              </div>
              <select
                value={z.check}
                onChange={(e) =>
                  setZones((all2) =>
                    all2.map((x, k) =>
                      k === i ? { ...x, check: e.target.value as LabelZone['check'] } : x,
                    ),
                  )
                }
                style={{ width: '100%', marginTop: 8 }}
              >
                {CHECKS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                {CHECKS.find((c) => c.id === z.check)?.hint}
              </div>
              {(z.check === 'text' || z.check === 'code') && (
                <div className="row" style={{ gap: 8, marginTop: 8 }}>
                  <input
                    value={z.expected ?? ''}
                    placeholder="Exact value (optional)"
                    onChange={(e) =>
                      setZones((all2) =>
                        all2.map((x, k) => (k === i ? { ...x, expected: e.target.value || null } : x)),
                      )
                    }
                    style={{ flex: 1 }}
                  />
                  {z.check === 'text' && (
                    <input
                      value={z.pattern ?? ''}
                      placeholder="Pattern e.g. ^L\\d{6}$"
                      onChange={(e) =>
                        setZones((all2) =>
                          all2.map((x, k) => (k === i ? { ...x, pattern: e.target.value || null } : x)),
                        )
                      }
                      style={{ flex: 1 }}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * What the scan found, offered one at a time.
 *
 * Deliberately NOT an "accept all" button and nothing else. Accept-all is the
 * feature that turns this from a help into a liability: the proposals differ in
 * how much they deserve trust — a decoded barcode is a fact, a `match` zone
 * typed from OCR on a curved surface is a guess — and one button flattens that
 * difference. Accept-all exists here only for the codes, which decoded or did
 * not.
 *
 * Every proposal shows what was actually READ. That is the check a person can
 * make in one glance: a box in the right place around text that says
 * "=S Orcs D WA" is visibly wrong, and the same box with "LOT 2026-A7734" in
 * it is visibly right.
 */
function ProposalPanel({
  proposals,
  scanning,
  scanned,
  canEdit,
  cropHint,
  onCrop,
  onRescan,
  onHover,
  onAccept,
  onDismiss,
}: {
  proposals: ZoneProposal[];
  scanning: string | null;
  scanned: boolean;
  canEdit: boolean;
  cropHint: boolean;
  onCrop: () => void;
  onRescan: () => void;
  onHover: (id: string | null) => void;
  onAccept: (p: ZoneProposal) => void;
  onDismiss: (p: ZoneProposal) => void;
}) {
  if (scanning) {
    return (
      <div className="panel" style={{ padding: 10 }}>
        <div className="row" style={{ gap: 8 }}>
          <span className="spinner" style={{ width: 14, height: 14 }} />
          <strong style={{ fontSize: 13 }}>Reading the label…</strong>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{scanning}</div>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          {scanned ? 'The scan found nothing to suggest.' : 'Not scanned yet.'}
        </span>
        <button className="btn sm" disabled={!canEdit} onClick={onRescan}>
          Scan label
        </button>
      </div>
    );
  }

  const codes = proposals.filter((p) => p.check === 'code');

  return (
    <div
      className="panel"
      style={{ padding: 10, borderColor: 'var(--accent)', display: 'grid', gap: 8 }}
    >
      <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>
          {proposals.length} suggestion{proposals.length === 1 ? '' : 's'}
        </strong>
        <button className="btn sm" disabled={!canEdit} onClick={onRescan}>
          Scan again
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12, lineHeight: 1.5 }}>
        Nothing here is saved. Check what was read, then keep the ones that are right.
      </div>

      {cropHint && (
        <div
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            background: 'var(--accent-dim)',
            border: '1px solid var(--accent)',
            borderRadius: 8,
            padding: '8px 10px',
            display: 'grid',
            gap: 6,
          }}
        >
          <span>
            Only the codes here — on a photo the printed fields can't be read until the background
            is out of the way. Draw a box around just the label and scan that.
          </span>
          <button className="btn sm" disabled={!canEdit} onClick={onCrop} style={{ justifySelf: 'start' }}>
            Scan a region
          </button>
        </div>
      )}

      {codes.length > 1 && (
        <button className="btn sm" disabled={!canEdit} onClick={() => codes.forEach(onAccept)}>
          Accept all {codes.length} codes
        </button>
      )}

      {proposals.map((p) => (
        <div
          key={p.id}
          onMouseEnter={() => onHover(p.id)}
          onMouseLeave={() => onHover(null)}
          style={{
            borderLeft: `3px solid ${CHECK_COLOUR[p.check] ?? '#3b82f6'}`,
            paddingLeft: 8,
            display: 'grid',
            gap: 4,
          }}
        >
          <div className="row" style={{ justifyContent: 'space-between', gap: 6 }}>
            <strong style={{ fontSize: 13 }}>{p.label}</strong>
            <span className="muted" style={{ fontSize: 11, fontFamily: 'var(--mono)' }}>
              {/* A decoded code is a fact; OCR is a reading. Say which. */}
              {p.check === 'code' ? 'decoded' : `${p.confidence}% sure`}
            </span>
          </div>
          <div
            style={{
              fontSize: 12,
              fontFamily: 'var(--mono)',
              wordBreak: 'break-all',
              color: 'var(--text-dim)',
            }}
          >
            {p.readText || '—'}
          </div>
          <div className="muted" style={{ fontSize: 11 }}>{p.reason}</div>
          <div className="row" style={{ gap: 6 }}>
            <button className="btn sm" disabled={!canEdit} onClick={() => onAccept(p)}>
              Keep
            </button>
            <button className="btn sm ghost" onClick={() => onDismiss(p)}>
              Discard
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function TestPanel({
  productId,
  reference,
  onTested,
  onError,
}: {
  productId: number;
  reference: LabelReference;
  onTested: () => void | Promise<void>;
  onError: (m: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<LabelResult | null>(null);
  const [busy, setBusy] = useState(false);
  const overlayUrl = useImageUrl(result?.overlay_key ?? '');

  const verdictColour =
    result?.verdict === 'GOOD' ? 'var(--good)' : result?.verdict === 'BAD' ? 'var(--bad)' : 'var(--warn)';

  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>3 · Test a pack</strong>
        <button
          className="btn primary sm"
          disabled={busy || reference.zones.length === 0}
          title={reference.zones.length === 0 ? 'Mark and save some zones first' : undefined}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Inspecting…' : 'Upload a pack to check'}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          try {
            setResult(await api.inspectLabel(productId, file));
            await onTested();
          } catch (err) {
            onError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      />

      {result && (
        <div style={{ marginTop: 14 }}>
          <div className="row" style={{ gap: 14, alignItems: 'baseline' }}>
            <span style={{ fontSize: 26, fontWeight: 800, color: verdictColour }}>
              {result.verdict}
            </span>
            <span className="muted" style={{ fontSize: 13 }}>
              {result.reason}
            </span>
            <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
              alignment {(result.align_score * 100).toFixed(0)}% · {result.latency_ms} ms
            </span>
          </div>

          {!result.aligned && (
            // UNSURE, never BAD. The pack may be perfect and merely turned
            // away, and rejecting it teaches operators to distrust the machine.
            <p style={{ color: 'var(--warn)', fontSize: 13, marginTop: 8 }}>
              ▲ The label could not be lined up with the reference, so this is UNSURE rather than a
              reject — the pack may be fine and simply turned away from the camera.
            </p>
          )}

          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 16, marginTop: 12 }}>
            {overlayUrl ? (
              <img
                src={overlayUrl}
                alt="checked"
                style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)' }}
              />
            ) : (
              <div />
            )}
            <table>
              <tbody>
                {result.zones.map((z) => (
                  <tr key={z.id}>
                    <td>
                      <strong>{z.label || z.id}</strong>
                      <div className="muted" style={{ fontSize: 11 }}>
                        {z.detail}
                        {z.found ? ` — read “${z.found}”` : ''}
                      </div>
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <span style={{ color: z.ok ? 'var(--good)' : 'var(--bad)' }}>
                        {z.ok ? '● pass' : '■ fail'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function History({ rows }: { rows: Record<string, unknown>[] }) {
  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <strong>Recent label checks</strong>
      <table style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>When</th>
            <th>Verdict</th>
            <th>Why</th>
            <th style={{ textAlign: 'right' }}>Alignment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={String(r.id)}>
              <td className="muted">{fmtTime(String(r.time))}</td>
              <td>
                <span className={`badge ${String(r.verdict).toLowerCase()}`}>{String(r.verdict)}</span>
              </td>
              <td className="muted" style={{ fontSize: 12 }}>{String(r.reason ?? '')}</td>
              <td className="muted" style={{ textAlign: 'right' }}>
                {(Number(r.alignScore) * 100).toFixed(0)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
